import React, { useEffect, useState } from "react";
import "./tasks.css";
import { toast } from "react-toastify";
import { getTasks, updateTaskStatus } from "../../service/project_service";
import CustomLoadingindicator from "../../components/custom_loading";
import {
  CheckCircle,
  Clock,
  Briefcase,
  CalendarDays,
  AlertCircle,
  ListChecks,
  Loader2,
} from "lucide-react";

const STATUS_PENDING = "Pending";
const STATUS_PROGRESS = "In Progress";
const STATUS_COMPLETED = "Completed";

function TaskCard({ task, updatingId, handleStatusUpdate, STATUS_PROGRESS, STATUS_COMPLETED }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const taskId = task.taskid || task.id || task.task_id;
  const status = task.status || STATUS_PENDING;
  const isProgress = status === STATUS_PROGRESS;
  const isCompleted = status === STATUS_COMPLETED;

  const cardClass = isCompleted ? "status-completed" : isProgress ? "status-progress" : "status-pending";
  const badgeClass = isCompleted ? "badge-completed" : isProgress ? "badge-progress" : "badge-pending";
  const priority = (task.priority || "").toLowerCase();
  const progressKey = `${taskId}-${STATUS_PROGRESS}`;
  const completedKey = `${taskId}-${STATUS_COMPLETED}`;

  const description = task.description || task.taskdescription;

  return (
    <div className={`task-card ${cardClass}`}>
      <div className="task-card-header">
        <span className="task-title">
          {task.taskname || task.task_name || task.title || "Untitled Task"}
        </span>
        <span className={`task-status-badge ${badgeClass}`}>
          {isCompleted ? <CheckCircle size={12} /> : isProgress ? <Loader2 size={12} className="animate-spin" /> : <Clock size={12} />}
          {status}
        </span>
      </div>

      <div className="task-card-body">
        {description && (
          <div className="task-description-wrapper">
            <p className={`task-description ${isExpanded ? "expanded" : ""}`}>
              {description}
            </p>
            {description.length > 150 && (
              <button
                className="read-more-btn"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? "Read Less" : "Read More"}
              </button>
            )}
          </div>
        )}

        <div className="task-meta">
          {(task.projectname || task.project?.projectname) && (
            <div className="task-meta-row">
              <Briefcase size={14} />
              <strong>Project</strong>
              <span>{task.projectname || task.project?.projectname}</span>
            </div>
          )}
          {(task.duedate || task.due_date || task.deadline) && (
            <div className="task-meta-row">
              <CalendarDays size={14} />
              <strong>Due Date</strong>
              <span>{new Date(task.duedate || task.due_date || task.deadline).toLocaleDateString("en-GB")}</span>
            </div>
          )}
          {task.priority && (
            <div className="task-meta-row">
              <AlertCircle size={14} />
              <strong>Priority</strong>
              <span className={`task-priority ${priority}`}>{task.priority}</span>
            </div>
          )}
        </div>
      </div>

      <div className="task-card-divider" />

      <div className="task-card-footer">
        <button
          className="task-btn task-btn-progress"
          disabled={isProgress || isCompleted || updatingId === progressKey}
          onClick={() => handleStatusUpdate(task, STATUS_PROGRESS)}
        >
          {updatingId === progressKey ? <Loader2 size={14} className="animate-spin" /> : <Loader2 size={14} />}
          {isProgress ? "In Progress" : updatingId === progressKey ? "Updating…" : "Progress"}
        </button>

        <button
          className="task-btn task-btn-completed"
          disabled={isCompleted || !isProgress || updatingId === completedKey}
          onClick={() => handleStatusUpdate(task, STATUS_COMPLETED)}
        >
          {updatingId === completedKey ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
          {isCompleted ? "Completed" : updatingId === completedKey ? "Updating…" : "Complete"}
        </button>
      </div>
    </div>
  );
}

function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await getTasks();
      if (response.status === "success" && response.data) {
        setTasks(response.data);
      } else {
        toast.error(response.message || "Failed to fetch tasks");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (task, newStatus) => {
    const taskId = task.taskid || task.id || task.task_id;
    setUpdatingId(`${taskId}-${newStatus}`);
    try {
      const response = await updateTaskStatus({
        taskid: taskId,
        status: newStatus,
      });
      if (response.status === "success") {
        toast.success(
          newStatus === STATUS_PROGRESS
            ? "Task marked as In Progress"
            : "Task marked as Completed",
        );
        setTasks((prev) =>
          prev.map((t) =>
            (t.taskid || t.id || t.task_id) === taskId
              ? { ...t, status: newStatus }
              : t,
          ),
        );
      } else {
        toast.error(response.message || "Failed to update task status");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) return <CustomLoadingindicator />;

  /* ---- Summary counts ---- */
  const totalTasks = tasks.length;
  const pendingCount = tasks.filter(
    (t) => !t.status || t.status === STATUS_PENDING,
  ).length;
  const progressCount = tasks.filter(
    (t) => t.status === STATUS_PROGRESS,
  ).length;
  const completedCount = tasks.filter(
    (t) => t.status === STATUS_COMPLETED,
  ).length;

  return (
    <div className="tasks-container">
      <div className="tasks-header">
        <h4>My Tasks</h4>
        <p>Tasks assigned to you by the admin</p>
      </div>

      {totalTasks > 0 && (
        <div className="tasks-summary">
          <div className="tasks-summary-chip">
            <ListChecks size={14} />
            Total: {totalTasks}
          </div>
          <div className="tasks-summary-chip">
            <span className="chip-dot" style={{ background: "#f59e0b" }} />
            Pending: {pendingCount}
          </div>
          <div className="tasks-summary-chip">
            <span className="chip-dot" style={{ background: "#3b82f6" }} />
            In Progress: {progressCount}
          </div>
          <div className="tasks-summary-chip">
            <span className="chip-dot" style={{ background: "#10b981" }} />
            Completed: {completedCount}
          </div>
        </div>
      )}

      {totalTasks === 0 ? (
        <div className="tasks-empty">
          <div className="tasks-empty-icon">📋</div>
          <h3>No Tasks Assigned</h3>
          <p>
            You have no tasks assigned yet. Tasks assigned by the admin will
            appear here.
          </p>
        </div>
      ) : (
        <div className="tasks-grid">
          {tasks.map((task) => (
            <TaskCard
              key={task.taskid || task.id || task.task_id}
              task={task}
              updatingId={updatingId}
              handleStatusUpdate={handleStatusUpdate}
              STATUS_PROGRESS={STATUS_PROGRESS}
              STATUS_COMPLETED={STATUS_COMPLETED}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default Tasks;
